import { QueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { MessageSquare, Send, ThumbsDown, ThumbsUp, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ChatMessage, ChatWidgetProps, DataResponse } from "../types";
import { formatRelativeTime } from "../utils/formatRelativeTime";

// Create a schema for chat creation
const createChatSchema = z.object({
  message: z.string(),
  conversation_id: z.string().optional(),
  organisation_id: z.string(),
});

const q = new QueryClient();
export const ChatWidget = ({
  organisationId,
  baseUrl = "https://api.opaper.app/lumi-server",
  initialExpanded = false,
  config,
}: ChatWidgetProps) => {
  return (
    <ChatWidget2
      organisationId={organisationId}
      baseUrl={baseUrl}
      initialExpanded={initialExpanded}
      config={config}
    />
  );
};

const ChatWidget2 = ({
  organisationId,
  initialExpanded,
  baseUrl,
  config,
}: ChatWidgetProps) => {
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState<Record<string, boolean>>({});
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Default configuration values
  const defaultConfig = {
    headerColor: "#3a49ee",
    primaryColor: "#3a49ee",
    maxHeight: "600px",
    maxWidth: "380px",
  };

  // Merge user config with defaults
  const widgetConfig = { ...defaultConfig, ...config };

  // Create dynamic styles based on configuration
  const dynamicStyles: React.CSSProperties = {
    "--widget-primary-color": widgetConfig.primaryColor,
    "--widget-header-color": widgetConfig.headerColor,
    "--widget-max-height": widgetConfig.maxHeight,
    "--widget-max-width": widgetConfig.maxWidth,
  } as React.CSSProperties;

  const { mutateAsync: submitChat } = useMutation(
    {
      mutationKey: ["submitChat"],
      mutationFn: async ({
        message,
        conversation_id,
        organisation_id,
      }: z.infer<typeof createChatSchema>) => {
        const resp = await fetch(`${baseUrl}/api/chats`, {
          method: "POST",
          body: JSON.stringify({ message, conversation_id, organisation_id }),
        });

        if (!resp.ok) {
          throw new Error("Failed to submit chat");
        }

        const respData = (await resp.json()) as DataResponse<ChatMessage>;

        return respData.data;
      },
    },
    q,
  );
  const { data: messages, refetch: refetchChats } = useQuery(
    {
      queryKey: ["conversationChats", conversationId],
      queryFn: async ({ queryKey: [, conversationId] }) => {
        if (!conversationId) {
          return [];
        }

        const resp = await fetch(`${baseUrl}/api/chats/${conversationId}`);

        if (!resp.ok) {
          throw new Error("Failed to fetch chats");
        }

        const data = (await resp.json()) as DataResponse<ChatMessage[]>;

        return data.data;
      },
      enabled: isExpanded && !!conversationId,
      refetchInterval: 3000,
    },
    q,
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isExpanded) {
      scrollToBottom();
    }
  }, [messages, isExpanded]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const data = await submitChat({
      message: input,
      conversation_id: conversationId ?? undefined,
      organisation_id: organisationId,
    });

    setConversationId(data.conversation_id);
    await refetchChats();
    setInput("");
  };

  const handleFeedback = async (messageId: string, isPositive: boolean) => {
    console.log("handleFeedback", messageId, isPositive);
  };

  const renderMessageContent = (text: string) => {
    const parts = text.split(/(!?\[.*?\]\(.*?\))/);
    return parts.map((part, index) => {
      const imageMatch = part.match(/!\[(.*?)\]\((.*?)\)/);
      if (imageMatch) {
        const [, alt, src] = imageMatch;
        return (
          <img key={index} src={src} alt={alt} className="message-image" />
        );
      }

      const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
      if (linkMatch) {
        const [, text, url] = linkMatch;
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="message-link"
          >
            {text}
          </a>
        );
      }

      return (
        <p key={index} className="message-text">
          {part}
        </p>
      );
    });
  };

  const handleStartNewChat = async () => {
    setConversationId(null);
    await refetchChats();
    setFeedback({});
    setIsExpanded(true);
  };

  return (
    <div className="lumi-chat-widget-host" style={dynamicStyles}>
      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className="widget-toggle-button"
        >
          <MessageSquare className="widget-toggle-icon" />
        </button>
      ) : (
        <div className="widget-expanded-panel">
          <div className="widget-header">
            <div className="header-content">
              <div>
                <div className="header-status">
                  <div className="status-indicator"></div>
                  <p className="status-text">Ready to assist you</p>
                </div>
              </div>
              <div className="header-actions">
                <button
                  onClick={handleStartNewChat}
                  className="header-button"
                  title="Start New Chat"
                >
                  <MessageSquare size={16} />
                </button>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="header-button"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>

          <div className="messages-area">
            {!conversationId && (
              <div className="message-row-ai">
                <div className="message-content-wrapper">
                  <div className={`message-bubble message-bubble-ai`}>
                    {renderMessageContent(
                      "Hi there! How can I help you today?",
                    )}
                  </div>
                </div>
              </div>
            )}
            {conversationId &&
              messages?.map((message) => (
                <div
                  key={message.id}
                  className={`message-row ${message.sender_type === "user" ? "message-row-user" : "message-row-ai"}`}
                >
                  {message.sender_type === "ai" && (
                    <div className="avatar-ai">AI</div>
                  )}
                  <div className="message-content-wrapper">
                    <div
                      className={`message-bubble ${message.sender_type === "user" ? "message-bubble-user" : "message-bubble-ai"}`}
                    >
                      {renderMessageContent(message.payload)}
                    </div>
                    {message.sender_type === "admin" && (
                      <div className="feedback-container">
                        <button
                          onClick={() => handleFeedback(message.id, true)}
                          className={`feedback-button ${feedback[message.id] === true ? "feedback-button-positive-selected" : "feedback-button-default"}`}
                          title="Helpful response"
                        >
                          <ThumbsUp size={12} />
                        </button>
                        <button
                          onClick={() => handleFeedback(message.id, false)}
                          className={`feedback-button ${feedback[message.id] === false ? "feedback-button-negative-selected" : "feedback-button-default"}`}
                          title="Unhelpful response"
                        >
                          <ThumbsDown size={12} />
                        </button>
                      </div>
                    )}
                    <div className="message-timestamp">
                      {formatRelativeTime(new Date(message.created_at))}
                    </div>
                  </div>
                  {message.sender_type === "user" && (
                    <div className="avatar-user">You</div>
                  )}
                </div>
              ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-area-container">
            <div className="input-form-wrapper">
              <form onSubmit={handleSubmit} className="input-form">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your message..."
                  className="message-input"
                />
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="send-button"
                >
                  <Send size={18} />
                </button>
              </form>
            </div>
            <div className="footer-container">
              <p className="footer-text">
                Powered by{" "}
                <a
                  href="https://www.thelumi.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  Lumi by Opaper
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
